import React, { FC, forwardRef } from 'react';
import MaterialTable, { Column, Action, MTableToolbar } from 'material-table';
import { Paper, Grid, makeStyles, IconButton, Avatar } from '@material-ui/core';
import TimePicker, { TimePickerValue } from 'react-time-picker';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAdd';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import AddIcon from '@material-ui/icons/Add';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { useLanguageContext } from '../languages';
import { CricketTeamConfig, Period, Team } from '../../utils/types';
import theme from '../../theme';
import SimpleTextInfo from '../common/SimpleTextInfo';
import DateCell from '../common/DateCell';
import { getLocalTimezone } from '../../utils/dateTransform';
import '../common/react-time-picker.css';
import { Alert } from '@material-ui/lab';
import Dropdown from '../common/Dropdown';
import csvDownload from 'json-to-csv-export';
import { flattenObject, formatEndScore } from './exportHelpers';
import SaveAltIcon from '@material-ui/icons/SaveAlt';

const unknownValue = '';

const getPeriodLengthInSeconds = (period: Period): number => {
	if (period.source?.startTime && period.source?.endTime) {
		return dayjs(period.source.endTime).diff(dayjs(period.source.startTime), 'second') + 1;
	}

	return 0;
};

const getPeriodsLengthInSeconds = (periods: Period[]): string => {
	let seconds = 0;
	periods.forEach((period: Period) => {
		seconds += getPeriodLengthInSeconds(period);
	});

	return `(${seconds}s)`;
};

const renderSegmentIdSection = (rowData: Period, sectionPosition: number): string => {
	const { startGameTime } = rowData;
	if (startGameTime) {
		const { segmentId } = startGameTime;
		if (segmentId) {
			const segmentArray = segmentId.split('.');
			return segmentArray[sectionPosition] ?? unknownValue;
		} else {
			return unknownValue;
		}
	} else {
		return unknownValue;
	}
};

const isPeriodEditorial = (rowData: Period, editorialPeriodPlayIds: string[]): boolean => {
	if (!editorialPeriodPlayIds || editorialPeriodPlayIds.length === 0 || !rowData.playId) return false;
	return editorialPeriodPlayIds.includes(rowData.playId!);
};

const isPeriodOriginUnknow = (periodPlayId: string, autoAndEditorialPlayIds: string[]): boolean => {
	// if autoAndEditorialPlayIds is empty then no checks applied
	return autoAndEditorialPlayIds.length > 0 ? !autoAndEditorialPlayIds.includes(periodPlayId) : false;
};

const toPeriod = (editablePeriod: EditablePeriod): Period => {
	const { firstSegment, secondSegment, thirdSegment, fourthSegment, data } = editablePeriod;
	if (data.startGameTime) {
		data.startGameTime.segmentId = [firstSegment, secondSegment, thirdSegment, fourthSegment].filter((item) => item !== unknownValue).join('.');
		data.startGameTime.segment = parseInt(firstSegment);
	}

	if (data.type === 'Text') data.source.endTime = data.source.startTime;
	data.source.startTime = data.source.startTime && dayjs(data.source.startTime).toISOString();
	data.source.endTime = data.source.endTime && dayjs(data.source.endTime).toISOString();

	return data;
};

export const sortAlphabetically = (gotList: string[]): string[] => {
	return gotList.sort((item1, item2) => item1.localeCompare(item2));
};

export const getSportTitleDropdown = (dropDownOptions: Record<string, unknown>, sport: string, type: string, leagueId: string): string[] => {
	const sportLower = sport?.toLowerCase() || undefined;

	if (sportLower === 'cricket') {
		return sortAlphabetically(dropDownOptions.cricketEventList ? (dropDownOptions.cricketEventList as string[]) : []);
	} else if (sportLower === 'golf') {
		return sortAlphabetically(dropDownOptions.golfEventList ? (dropDownOptions.golfEventList as string[]) : []);
	} else if (sportLower === 'football') {
		return sortAlphabetically(dropDownOptions.footballEventList ? (dropDownOptions.footballEventList as string[]) : []);
	} else if (sportLower === 'f1' && type === 'Action') {
		return sortAlphabetically(dropDownOptions.f1ActionEventList ? (dropDownOptions.f1ActionEventList as string[]) : []);
	} else if (sportLower === 'f1') {
		return sortAlphabetically(dropDownOptions.f1TextEventList ? (dropDownOptions.f1TextEventList as string[]) : []);
	} else if (sportLower === 'americanfootball') {
		return sortAlphabetically(dropDownOptions.americanfootballEventList ? (dropDownOptions.americanfootballEventList as string[]) : []);
	} else if (sportLower === 'basketball') {
		return sortAlphabetically(dropDownOptions.basketballEventList ? (dropDownOptions.basketballEventList as string[]) : []);
	} else if (sportLower === 'baseball') {
		return sortAlphabetically(dropDownOptions.baseballEventList ? (dropDownOptions.baseballEventList as string[]) : []);
	}

	return [];
};

export const getSportTeamDropdown = (
	dropDownOptions: Record<string, unknown>,
	sport: string,
	type: string,
	leagueId: string | undefined,
	teamId: string | undefined,
	teams?: string[]
): string[] => {
	const sportLower = sport?.toLowerCase() || undefined;

	if (sportLower === 'cricket' && type === 'Action' && teams) {
		return sortAlphabetically(teams);
	} else if (sportLower === 'f1' && type === 'Action') {
		return sortAlphabetically(dropDownOptions.f1TeamsList ? (dropDownOptions.f1TeamsList as string[]) : []);
	}

	return [];
};

export const getSportShortTextDropdown = (
	dropDownOptions: Record<string, unknown>,
	sport: string,
	type: string,
	leagueId: string | undefined,
	teamId: string | undefined
): string[] => {
	const sportLower = sport?.toLowerCase() || undefined;

	if (sportLower === 'cricket' && type === 'Action') {
		let playerList: string[] = [];

		if (dropDownOptions.cricketTeamsList) {
			(dropDownOptions.cricketTeamsList as CricketTeamConfig[]).forEach((team) => {
				if (teamId) {
					const idList = team.id.split('||');
					if (idList.includes(teamId)) {
						playerList = [...playerList, ...team.players];
					}
				} else {
					playerList = [...playerList, ...team.players];
				}
			});
		}

		return sortAlphabetically(playerList);
	} else if (sportLower === 'f1' && type === 'Action') {
		return sortAlphabetically(dropDownOptions.f1DriverNameList ? (dropDownOptions.f1DriverNameList as string[]) : []);
	} else if (sportLower === 'golf' && type === 'Action') {
		return sortAlphabetically(dropDownOptions.golfPlayerNameList ? (dropDownOptions.golfPlayerNameList as string[]) : []);
	}

	return [];
};

const useStyles = makeStyles((theme) => ({
	spacing: {
		marginBottom: theme.spacing(2)
	},
	dropdowns: {
		minWidth: '125px'
	},
	timePicker: {
		display: 'inline-block',
		minWidth: '100px'
	},
	primaryButton: {
		backgroundColor: theme.palette.primary.main
	}
}));

const Periods: FC<PeriodsProps> = (props) => {
	const {
		hideDetails,
		teams,
		sport,
		leagueId,
		gameStartTime,
		currentPeriod,
		checkPlayIdOrigin,
		editorialPeriodPlayIds,
		draftPeriodPlayIds,
		gameTitle,
		highlightId,
		onRowAdd,
		onRowPick,
		onRowDelete,
		onAllDelete,
		onRowPlay,
		onRowUpdate,
		periods
	} = props;

	const classes = useStyles();
	const { tableLocalization } = useLanguageContext();

	// prettier-ignore
	const actions = [
        (rowData: EditablePeriod): Action<EditablePeriod> => ({
            icon: (): JSX.Element => (rowData.data.type === 'Text' ? <TextFieldsIcon /> : <PlayArrowIcon />),
            tooltip: rowData.data.type === 'Text' ? 'Text Event' : 'Play',
            isFreeAction: false,
            disabled: rowData.data.type === 'Text' || !onRowPlay,
            onClick: (
                event: unknown,
                _data: EditablePeriod | EditablePeriod[]
            ): void => {
                const data =
                    _data instanceof Array
                        ? _data.map((item) => item.data)[0]
                        : _data.data;
                onRowPlay && onRowPlay(event, data);
            },
        }),
        ...(onRowPick
            ? [
                {
                    icon: (): JSX.Element => <AddIcon data-testid="select-period-icon" />,
                    tooltip: 'Select',
                    onClick: (
                        event: unknown,
                        _data: EditablePeriod | EditablePeriod[]
                    ): void => {
                        const data =
                            _data instanceof Array
                                ? _data.map((item) => item.data)
                                : [_data.data];
                        onRowPick(event, data);
                    },
                },
                {
                    icon: (): JSX.Element => <PlaylistAddCheckIcon />,
                    isFreeAction: true,
                    tooltip: 'Select all',
                    onClick: (
                        event: unknown,
                        _data: EditablePeriod | EditablePeriod[]
                    ): void => {
                        onRowPick(event, periods);
                    },
                },
            ]
            : []),
        ...(onAllDelete
            ? [
                {
                    icon: (): JSX.Element => <DeleteSweepIcon />,
                    isFreeAction: true,
                    tooltip: 'Remove all',
                    onClick: (
                        event: unknown,
                        _data: EditablePeriod | EditablePeriod[]
                    ): void => {
                        onAllDelete();
                    },
                },
            ]
            : [])
    ];

	const playIds = periods
		.filter((period): boolean => period.type !== 'Text')
		.map((period) => period.playId)
		.filter((playId) => playId) as string[];

	const unknowOriginPlayIds: string[] =
		draftPeriodPlayIds && draftPeriodPlayIds.length > 0 && checkPlayIdOrigin
			? playIds.filter((playId) => isPeriodOriginUnknow(playId, draftPeriodPlayIds))
			: [];

	const getIdByTeamName: { [key: string]: string } = {};
	teams &&
		teams.forEach((team) => {
			if (team.name) getIdByTeamName[team.name] = team.id!;
		});

	const updateTeamIdAndName = (rowData: EditablePeriod, newTeamNameValue: string | null): EditablePeriod => {
		if (newTeamNameValue && rowData.data.team) {
			rowData.data.team.name = newTeamNameValue;
			rowData.data.team.id = getIdByTeamName[newTeamNameValue];
		}
		return rowData;
	};

	const getSportColumns = (): Column<EditablePeriod>[] => {
		const sportLower = sport?.toLowerCase() || undefined;

		if (sportLower === 'golf') {
			return [
				{
					title: 'Round',
					field: 'firstSegment',
					type: 'string',
					filtering: false
				},
				{
					title: 'Hole',
					field: 'secondSegment',
					type: 'string',
					filtering: false
				},
				{
					title: 'Yardage',
					field: 'thirdSegment',
					type: 'string',
					filtering: false
				}
			];
		} else if (sportLower === 'football' || sportLower === 'americanfootball') {
			return [
				{
					title: 'Team',
					field: 'data.logoUrl',
					editable: 'never',
					filtering: false,
					render: (row: EditablePeriod): JSX.Element => <img src={row.data.team?.logoUrl || ''} alt={row.data.team?.name || ''} width='40px' />
				},
				{
					title: 'Segment',
					field: 'firstSegment',
					editable: 'never',
					filtering: false
				}
			];
		} else if (sportLower === 'f1') {
			return [
				{
					title: 'Lap',
					field: 'firstSegment',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					}
				},
				{
					title: 'Team',
					field: 'data.team.id',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					},
					editComponent: (props): JSX.Element => (
						<Dropdown
							autoSelect={true}
							freeSolo={true}
							onChange={(event: unknown, newValue: string | null): void => props.onChange(newValue)}
							getDropdownFunction={getSportTeamDropdown}
							type={props.rowData.data?.type ?? ''}
							sport={sport}
							editComponentProps={props}
							classes={classes}
						/>
					)
				}
			];
		} else if (sportLower === 'cricket') {
			return [
				{
					title: 'Innings',
					field: 'firstSegment',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					}
				},
				{
					title: 'Balls',
					field: 'secondSegment',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					}
				},
				{
					title: 'Wickets',
					field: 'thirdSegment',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					}
				},
				{
					title: 'Overs',
					field: 'fourthSegment',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					}
				},
				{
					title: 'Team',
					field: 'data.team.name',
					filtering: false,
					editable: (_, rowData: EditablePeriod): boolean => {
						const isNewPeriod = rowData === undefined;
						const isTextPeriod = rowData?.data?.type === 'Text';
						return !isNewPeriod && !isTextPeriod;
					},
					editComponent: (props): JSX.Element => (
						<Dropdown
							autoSelect={true}
							onChange={(event: unknown, newValue: string | null): void => props.onRowDataChange(updateTeamIdAndName(props.rowData, newValue))}
							getDropdownFunction={getSportTeamDropdown}
							type={props.rowData.data?.type ?? ''}
							teams={Object.keys(getIdByTeamName)}
							sport={sport}
							editComponentProps={props}
							classes={classes}
						/>
					)
				}
			];
		}

		return [];
	};

	const isEditorialClipColumn = {
		title: 'Editorial',
		field: 'editorial',
		type: 'boolean',
		filtering: true
	};

	// prettier-ignore
	const columns = [
        {
            title: 'Title',
            field: 'data.title',
            filtering: false,
            editComponent: (props): JSX.Element => (
                <Dropdown
                    autoSelect={true}
                    freeSolo={(sport?.toLocaleLowerCase() === 'golf') ? false : true}
                    disableClearable={true}
                    onChange={(event: unknown, newValue: string | null): void => props.onChange(newValue)}
                    getDropdownFunction={getSportTitleDropdown}
                    type={props.rowData.data?.type ?? ''}
                    required={true}
                    sport={sport}
                    leagueId={leagueId ?? ''}
                    editComponentProps={props}
                    classes={classes}
                />
            ),
        },
        {
            title:
                sport?.toLowerCase() === 'f1' ? 'Driver name' : 'Player name',
            field: 'data.shortText',
            filtering: false,
            editable: (_, rowData: EditablePeriod): boolean => {
                const isNewPeriod = rowData === undefined;
                const isTextPeriod = rowData?.data?.type === 'Text';
                return !isNewPeriod && !isTextPeriod;
            },
            editComponent: (props): JSX.Element => (
                <Dropdown
                    autoSelect={true}
                    freeSolo={sport?.toLowerCase() !== 'f1'}
                    disableClearable={
                        sport?.toLowerCase() !== 'f1' &&
                        sport?.toLowerCase() !== 'cricket'
                    }
                    selectOnFocus={false}
                    onChange={(event: unknown, newValue: string | null): void => props.onChange(newValue)}
                    getDropdownFunction={getSportShortTextDropdown}
                    type={props.rowData.data?.type ?? ''}
                    teamId={props.rowData.data?.team?.id}
                    required={true}
                    sport={sport}
                    leagueId={leagueId ?? undefined}
                    editComponentProps={props}
                    classes={classes}
                />
            ),
        },
        {
            title: `Time (${getLocalTimezone()})`,
            field: 'data.source.startTime',
            filtering: false,
            editable: (_, rowData: EditablePeriod): boolean => {
                const isNewPeriod = rowData === undefined;
                const isTextPeriod = rowData?.data?.type === 'Text';
                return isNewPeriod || isTextPeriod;
            },
            editComponent: (props): JSX.Element => {
                if (!props.value) props.onChange(dayjs(gameStartTime).toDate()); // workaround

                return (
                    <TimePicker
                        className={classes.timePicker}
                        name="startTime"
                        value={dayjs(props.value || gameStartTime).toDate()}
                        format="HH:mm:ss"
                        maxDetail="second"
                        onChange={(value: TimePickerValue): void => {
                            if (typeof value === 'string') {
                                const timeParts = value.split(':');
                                const newValue = dayjs(gameStartTime)
                                    .set('hour', Number(timeParts[0]))
                                    .set('minute', Number(timeParts[1]))
                                    .set('second', Number(timeParts[2]))
                                    .toDate();
                                props.onChange(newValue);
                            }
                        }}
                        clearIcon={null}
                        disableClock={true}
                    />
                );
            },
            render: (row: EditablePeriod): JSX.Element => (
                <DateCell
                    value={row.data.source?.startTime ?? ''}
                    showTimezone={false}
                    showTimeOnly={true}
                />
            ),
        },
        {
            title:
                sport?.toLowerCase() === 'f1'
                    ? 'Position'
                    : sport?.toLowerCase() === 'cricket'
                        ? 'Runs'
                        : 'Score',
            field: 'data.endScore',
            filtering: false,
            editable: (_, rowData: EditablePeriod): boolean => {
                const isNewPeriod = rowData === undefined;
                const isTextPeriod = rowData?.data?.type === 'Text';
                return !isNewPeriod && !isTextPeriod;
            },
        },
        ...(sport ? getSportColumns() : []),
        ...((unknowOriginPlayIds.length)
            ? [{
                title: 'Warn',
                field: 'warn',
                type: 'boolean',
            }]
            : [...(editorialPeriodPlayIds && editorialPeriodPlayIds.length && !checkPlayIdOrigin
                ? [isEditorialClipColumn]
                : [])]),
    ] as Column<EditablePeriod>[];

	const detailsPanel = (rowData: EditablePeriod): JSX.Element => {
		const period = rowData.data;

		return (
			<Grid container direction='column' spacing={2} style={{ margin: theme.spacing(2) }}>
				<SimpleTextInfo label='Period ID' value={period.id || 'Not specified'} compact />
				<SimpleTextInfo label='Play ID' value={period.playId || 'Not specified'} compact />
				<SimpleTextInfo label='Description' value={period.description || 'Not specified'} compact />
				<SimpleTextInfo label='Type' value={period.type === 'Text' ? 'Text Event' : 'Video Event'} compact />
			</Grid>
		);
	};

	const handlePeriodAdd = async (newData: EditablePeriod): Promise<unknown> => {
		if (onRowAdd) {
			const { title, source } = newData.data;
			const startTime = source.startTime && dayjs(source.startTime).toISOString();

			return await onRowAdd({
				title,
				source: {
					...source,
					startTime,
					endTime: startTime,
					type: 'Text'
				},
				id: uuidv4(),
				playId: uuidv4(),
				type: 'Text'
			});
		}
	};

	const handlePeriodUpdate = async (newData: EditablePeriod, oldData?: EditablePeriod): Promise<unknown> => {
		if (onRowUpdate) {
			const newPeriod = toPeriod(newData);
			return await onRowUpdate(newPeriod, oldData?.data);
		}
	};

	const handlePeriodDelete = async (oldPeriod: EditablePeriod): Promise<unknown> => {
		if (onRowDelete) {
			return await onRowDelete(oldPeriod.data);
		}
	};

	const rowStyle = (rowData: EditablePeriod): CSSProperties => {
		let styles = {};

		if (rowData.data === currentPeriod) {
			styles = {
				...styles,
				backgroundColor: theme.palette.primary.main,
				color: 'white'
			};
		}

		if (rowData.editorial) {
			styles = { ...styles, fontWeight: 'bold' };
		}

		if (rowData.warn) {
			styles = { ...styles, backgroundColor: 'yellow', color: theme.palette.error.main };
		}

		return styles;
	};

	const videoPeriods = periods.filter((period) => period.type !== 'Text');

	const exportHighlight = (): void => {
		const flattenedData = periods?.map((item): Record<string, unknown> | undefined => {
			if (item.endScore) item.endScore = formatEndScore(item.endScore);
			if (sport && !['basketball', 'baseball'].includes(sport.toLowerCase())) {
				delete item.excitementScore;
				delete item.excitementThreshold;
			}
			return flattenObject(item);
		});
		if (flattenedData) csvDownload({ data: flattenedData, delimiter: ',', filename: `${gameTitle}-${highlightId}` });
	};

	return (
		<div className={classes.spacing}>
			<MaterialTable
				localization={tableLocalization}
				actions={actions}
				columns={columns}
				detailPanel={hideDetails ? undefined : detailsPanel}
				editable={{
					onRowAdd: onRowAdd ? handlePeriodAdd : undefined,
					onRowDelete: onRowDelete ? handlePeriodDelete : undefined,
					onRowUpdate: onRowUpdate ? handlePeriodUpdate : undefined
				}}
				icons={{
					Export: forwardRef(() => (
						<IconButton size='small'>
							<Avatar className={classes.primaryButton}>
								<SaveAltIcon />
							</Avatar>
						</IconButton>
					))
				}}
				data={periods.map((period) => {
					const common = {
						data: period,
						firstSegment: renderSegmentIdSection(period, 0),
						secondSegment: renderSegmentIdSection(period, 1),
						thirdSegment: renderSegmentIdSection(period, 2),
						fourthSegment: renderSegmentIdSection(period, 3)
					};

					return unknowOriginPlayIds.length && period.playId
						? {
								...common,
								warn: unknowOriginPlayIds.includes(period.playId)
							}
						: {
								...common,
								editorial: isPeriodEditorial(period, editorialPeriodPlayIds || [])
							};
				})}
				options={{
					pageSize: 5,
					search: true,
					sorting: false,
					rowStyle,
					filtering: editorialPeriodPlayIds && editorialPeriodPlayIds.length && !checkPlayIdOrigin ? true : false,
					searchFieldStyle: {
						width: '150px'
					},
					exportAllData: true,
					exportButton: {
						csv: true,
						pdf: false
					},
					exportCsv: exportHighlight,
					emptyRowsWhenPaging: false
				}}
				title={`Events: ${periods.length} ${getPeriodsLengthInSeconds(videoPeriods)}`}
				components={
					unknowOriginPlayIds.length
						? {
								Container: (props): JSX.Element => <Paper {...props} data-testid='periods-table' />,
								Toolbar: (props): JSX.Element => (
									<>
										<MTableToolbar {...props} />
										<Alert severity='warning'>
											Items (<strong>Red Text and Highlighted</strong>): Could not be found in any automated and editorial highlights.
											<br />
											These items will not be copied over and appear in other territory's airing (e.g. in the case of F1).
											<br />
											If the item was an editorial clip which was removed then recreated: Please remove such clip items, and under{' '}
											<strong>Select a Source Highlight</strong> select the latest <strong>DRAFT</strong> highlight to add the most up-to-date clip.
										</Alert>
									</>
								)
							}
						: {
								Container: (props): JSX.Element => <Paper {...props} data-testid='periods-table' />
							}
				}
			/>
		</div>
	);
};

type EditablePeriod = {
	data: Period;
	firstSegment: string;
	secondSegment: string;
	thirdSegment: string;
	fourthSegment: string;
	editorial?: boolean;
	warn?: boolean;
};

type PeriodsProps = {
	teams?: Team[] | null;
	sport?: string | null;
	leagueId?: string | null;
	gameStartTime?: string | undefined;
	currentPeriod?: Period;
	periods: Period[];
	hideDetails?: boolean | null;
	checkPlayIdOrigin?: boolean | null;
	editorialPeriodPlayIds?: string[] | null;
	draftPeriodPlayIds?: string[] | null;
	gameTitle?: string | null;
	highlightId?: string | null;
	onRowAdd?: (newData: Period) => Promise<unknown>;
	onRowPick?: (event: unknown, data: Period | Period[]) => void;
	onRowDelete?: (oldData: Period) => Promise<unknown>;
	onAllDelete?: () => Promise<unknown>;
	onRowPlay?: (event: unknown, data: Period) => void;
	onRowUpdate?: (newData: Period, oldData?: Period) => Promise<unknown>;
};

export default Periods;
